"use client";

import { useRef, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  Code2,
  Palette,
  Smartphone,
  Video,
  Mic,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const features = [
  {
    icon: Code2,
    title: "Vibe Coding",
    description:
      "AI pair programming with multi-agent orchestration. Plan, implement, and test complex systems with intelligent agents.",
    color: "#3b82f6",
    gradient: "from-blue-500 to-cyan-500",
  },
  {
    icon: Palette,
    title: "Vibe Designer",
    description:
      "AI-powered design tool with Figma integration. Generate beautiful, responsive UI components from text.",
    color: "#8b5cf6",
    gradient: "from-violet-500 to-purple-500",
  },
  {
    icon: Smartphone,
    title: "Vibe Mobile",
    description:
      "Cross-platform mobile app development. Build iOS and Android apps simultaneously with AI assistance.",
    color: "#06b6d4",
    gradient: "from-cyan-500 to-teal-500",
  },
  {
    icon: Video,
    title: "Vibe Motion",
    description:
      "AI video generation and editing. Create stunning motion graphics and video content with simple prompts.",
    color: "#ec4899",
    gradient: "from-pink-500 to-rose-500",
  },
  {
    icon: Mic,
    title: "Vibe Voice",
    description:
      "Voice assistant and audio features. Control your environment with voice and generate audio content.",
    color: "#10b981",
    gradient: "from-emerald-500 to-green-500",
  },
];

export function FeaturesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-10%" });
  const reducedMotion = useReducedMotion();

  return (
    <section
      ref={sectionRef}
      id="features"
      className="relative min-h-screen w-full py-32 overflow-hidden"
    >
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#16213e] via-[#0f0f23] to-[#1a1a2e]" />

        {/* Moving stars */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-0.5 h-0.5 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0.2, 1, 0.2],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Gradient orbs */}
        <motion.div
          className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 70%)",
            filter: "blur(80px)",
          }}
          animate={{ y: [0, 50, 0], x: [0, 30, 0] }}
          transition={{ duration: 15, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(236,72,153,0.2) 0%, transparent 70%)",
            filter: "blur(80px)",
          }}
          animate={{ y: [0, -50, 0], x: [0, -30, 0] }}
          transition={{ duration: 12, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div
            className="inline-flex items-center gap-2 px-5 py-2 bg-white/5 border border-white/10 rounded-full mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
          >
            <Sparkles className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-medium text-white/80">The Power Suite</span>
          </motion.div>

          <motion.h2
            className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <span className="block text-white">EVERYTHING YOU NEED</span>
            <span className="block bg-gradient-to-r from-violet-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
              IN ONE PLATFORM
            </span>
          </motion.h2>

          <motion.p
            className="max-w-2xl mx-auto text-lg text-white/50"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.4 }}
          >
            Five powerful tools unified under one AI-powered platform
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className={`relative group ${index === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{
                delay: reducedMotion ? 0 : 0.1 * index,
                duration: 0.6,
              }}
            >
              {/* Card glow on hover */}
              <motion.div
                className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500"
                style={{
                  background: `linear-gradient(135deg, ${feature.color}40, transparent)`,
                  filter: "blur(10px)",
                }}
              />

              <div className="relative h-full p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 group-hover:border-white/20 group-hover:bg-white/10">
                {/* Background gradient */}
                <div
                  className="absolute top-0 right-0 w-40 h-40 rounded-full blur-[80px] opacity-20 group-hover:opacity-40 transition-opacity"
                  style={{ background: feature.color }}
                />

                {/* Icon with animated ring */}
                <div className="relative mb-6">
                  <motion.div
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} p-[1px]`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <div className="w-full h-full rounded-2xl bg-[#0f0f23] flex items-center justify-center">
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                  </motion.div>

                  {/* Animated ring */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl border-2 opacity-0 group-hover:opacity-100"
                    style={{ borderColor: feature.color }}
                    animate={{ scale: [1, 1.2], opacity: [0.5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold mb-3">
                  <span
                    className={`bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent`}
                  >
                    {feature.title}
                  </span>
                </h3>

                {/* Description */}
                <p className="text-sm text-white/50 leading-relaxed mb-4">
                  {feature.description}
                </p>

                {/* Learn more link */}
                <motion.div
                  className="flex items-center gap-2 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: feature.color }}
                >
                  <span>Learn more</span>
                  <ArrowRight className="w-4 h-4" />
                </motion.div>

                {/* Bottom gradient line */}
                <motion.div
                  className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${feature.gradient}`}
                  initial={{ width: "0%" }}
                  whileInView={{ width: "100%" }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.8 }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default FeaturesSection;
